import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        console.log('🔄 Getting initial session...')
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          setLoading(false)
          return
        }

        if (session?.user) {
          console.log('✅ Found session for user:', session.user.email)
          setUser(session.user)
          // Load profile but don't block initial load
          loadUserProfile(session.user.id).catch(console.error)
        } else {
          console.log('ℹ️ No session found')
        }
      } catch (error) {
        console.error('Session error:', error)
      } finally {
        console.log('✅ Initial session check complete, setting loading to false')
        setLoading(false)
        setInitialLoad(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event, session?.user?.email)
        
        try {
          if (session?.user) {
            setUser(session.user)
            // Load profile but don't let it block authentication
            loadUserProfile(session.user.id).catch(console.error)
          } else {
            setUser(null)
            setProfile(null)
          }
        } catch (error) {
          console.error('Auth state change error:', error)
        } finally {
          // Always set loading to false regardless of profile loading
          setLoading(false)
          console.log('✅ Auth loading set to false')
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      if (error && error.code === 'PGRST116') {
        // Profile not found - create it automatically
        console.log('📝 Profile not found, creating automatically for user:', userId)
        try {
          const { data: userData } = await supabase.auth.getUser()
          if (userData?.user) {
            await supabase.rpc('create_user_profile', {
              user_id: userId,
              user_email: userData.user.email,
              display_name: userData.user.email.split('@')[0]
            })
            
            // Retry loading the profile
            const { data: newProfile } = await supabase
              .from('users')
              .select('*')
              .eq('id', userId)
              .single()
              
            if (newProfile) {
              setProfile(newProfile)
              console.log('✅ Profile created and loaded successfully')
            }
          }
        } catch (createError) {
          console.error('❌ Failed to create profile:', createError)
        }
        return
      }

      if (error) {
        console.error('Error loading profile:', error)
        return
      }

      if (data) {
        setProfile(data)
      }
    } catch (error) {
      console.error('Profile loading error:', error)
    }
  }

  const signUp = async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: options.displayName || email.split('@')[0]
          }
        }
      })

      if (error) {
        return { data: null, error }
      }

      // If signup succeeded but user needs confirmation, create profile manually
      if (data.user && !data.session) {
        try {
          await supabase.rpc('create_user_profile', {
            user_id: data.user.id,
            user_email: email,
            display_name: options.displayName || email.split('@')[0]
          })
        } catch (profileError) {
          console.warn('Profile creation failed, will retry on confirmation:', profileError)
        }
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error.message } }
    }
  }

  const signIn = async (email, password) => {
    try {
      console.log('🔑 AuthContext signIn called with:', email)
      
      // Create a Promise race with timeout to handle hanging auth
      const authPromise = supabase.auth.signInWithPassword({
        email,
        password
      })
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Auth request timed out')), 5000)
      })
      
      const { data, error } = await Promise.race([authPromise, timeoutPromise])

      console.log('🔐 Supabase signIn result:', { success: !error, hasSession: !!data?.session, error: error?.message })

      if (error) {
        console.log('❌ SignIn error, returning:', error)
        return { data: null, error }
      }

      console.log('✅ SignIn success, returning data')
      return { data, error: null }
    } catch (error) {
      console.error('💥 SignIn exception:', error)
      
      // If auth hangs but user is actually signed in, handle it
      if (error.message === 'Auth request timed out') {
        console.log('🕐 Auth timed out, checking current session...')
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user) {
            console.log('✅ User is actually signed in despite timeout')
            return { data: { session, user: session.user }, error: null }
          }
        } catch (sessionError) {
          console.error('Failed to check session:', sessionError)
        }
      }
      
      return { data: null, error: { message: error.message } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      
      if (!error) {
        setUser(null)
        setProfile(null)
      }
      
      return { error }
    } catch (error) {
      return { error: { message: error.message } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }

    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) {
        return { data: null, error }
      }

      setProfile(data)
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error.message } }
    }
  }

  const checkPermission = (requiredRole = 'free') => {
    if (!profile) return false

    const roleHierarchy = { free: 0, pro: 1, admin: 2 }
    const userRoleLevel = roleHierarchy[profile.role] || 0
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0

    return userRoleLevel >= requiredRoleLevel
  }

  const getProjectLimit = () => {
    if (!profile) return 0

    // Beta users get Pro benefits
    if (profile.beta_user || profile.role === 'pro' || profile.role === 'admin') {
      return -1 // Unlimited
    } else {
      return 10 // Free plan limit
    }
  }

  const refreshProfile = async () => {
    if (user) {
      await loadUserProfile(user.id)
    }
  }

  const value = {
    user,
    profile,
    signUp,
    signIn,
    signOut,
    updateProfile,
    checkPermission,
    getProjectLimit,
    refreshProfile,
    loading,
    // Helper getters
    isAuthenticated: !!user,
    isFree: profile?.role === 'free',
    isPro: profile?.role === 'pro',
    isAdmin: profile?.role === 'admin'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}